/**
 * Getting Started onboarding page — shown after terms acceptance, before dashboard.
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { authApi } from '../services/api'

export const GettingStarted = () => {
  const { refetch } = useAuth()
  const [completing, setCompleting] = useState(false)

  const handleComplete = async () => {
    setCompleting(true)
    try {
      await authApi.completeOnboarding()
      await refetch()
    } catch {
      // Silently proceed — worst case they see this page again
      await refetch()
    }
  }

  return (
    <div className="min-h-[calc(100vh-200px)] bg-gradient-to-br from-slate-50 via-emerald-50 to-blue-50 -m-6 p-6">
      <div className="max-w-4xl mx-auto py-8">
        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Welcome to Fact Checker</h1>
          <p className="text-lg text-gray-600">Here&apos;s how to get started</p>
        </div>

        {/* Steps */}
        <div className="grid md:grid-cols-3 gap-6 mb-10">
          {/* Step 1 */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600 font-bold">
                1
              </div>
              <h2 className="text-lg font-semibold text-gray-900">Explore the Dashboard</h2>
            </div>
            <p className="text-gray-600 text-sm leading-relaxed mb-4">
              Browse verification results, upload documents for fact-checking, and add human
              validation overrides to AI assessments.
            </p>
            <Link
              to="/results"
              className="text-sm text-emerald-600 hover:text-emerald-700 font-medium"
            >
              View Results →
            </Link>
          </div>

          {/* Step 2 */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center text-purple-600 font-bold">
                2
              </div>
              <h2 className="text-lg font-semibold text-gray-900">Connect Your AI Tools</h2>
            </div>
            <p className="text-gray-600 text-sm leading-relaxed mb-3">
              Use fact-checking directly in Claude Desktop, Claude Code, or ChatGPT via the Model
              Context Protocol.
            </p>
            <code className="block text-xs bg-gray-50 text-gray-600 px-3 py-2 rounded font-mono mb-3 break-all">
              https://factchecker.scientistintheloop.com/mcp
            </code>
            <Link
              to="/mcp-setup"
              className="text-sm text-emerald-600 hover:text-emerald-700 font-medium"
            >
              Full Setup Guide →
            </Link>
          </div>

          {/* Step 3 */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center text-emerald-600 font-bold">
                3
              </div>
              <h2 className="text-lg font-semibold text-gray-900">Verify Your First Claim</h2>
            </div>
            <p className="text-gray-600 text-sm leading-relaxed mb-4">
              Upload a document or connect via MCP and ask your AI assistant to fact-check a claim
              against its source.
            </p>
            <Link
              to="/upload"
              className="text-sm text-emerald-600 hover:text-emerald-700 font-medium"
            >
              Upload a Document →
            </Link>
          </div>
        </div>

        {/* Action buttons */}
        <div className="text-center space-y-3">
          <button
            onClick={handleComplete}
            disabled={completing}
            className="bg-emerald-600 text-white py-3 px-8 rounded-lg font-medium hover:bg-emerald-700 disabled:opacity-50 transition-colors"
          >
            {completing ? 'Loading...' : 'Go to Dashboard'}
          </button>
          <div>
            <button
              onClick={handleComplete}
              disabled={completing}
              className="text-sm text-gray-400 hover:text-gray-600 transition-colors"
            >
              Skip for now
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
